import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { FinanceLedgerService } from '../../../services/finance-ledger/finance-ledger.service';
import { FINANCE_TABS, FinanceTab } from '../finance-tabs';
import { Contract, ContractReportConfig, ContractSummaryReport } from '../../../services/ledger-client/types';
import { UsersService } from '../../../services/users/users.service';

type DateRangePreset =
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'last-quarter'
  | 'this-year'
  | 'ytd'
  | 'last-year'
  | 'last-30'
  | 'last-90'
  | 'trailing-12'
  | 'custom';

type StatementScope = 'both' | 'profit-loss' | 'balance-sheet';

interface StatementLine {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountTypeCode: string;
  amount: number;
}

interface ProfitLossStatement {
  revenueLines: StatementLine[];
  expenseLines: StatementLine[];
  totalRevenue: number;
  totalExpenses: number;
  netIncome: number;
}

interface BalanceSheetStatement {
  assetLines: StatementLine[];
  liabilityLines: StatementLine[];
  totalAssets: number;
  totalLiabilities: number;
  currentEarnings: number;
  totalLiabilitiesAndEquity: number;
  imbalance: number;
  isBalanced: boolean;
}

interface OutstandingBalanceLine {
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: number;
}

interface OutstandingPayablesReceivables {
  asOfDate: string;
  receivableLines: OutstandingBalanceLine[];
  payableLines: OutstandingBalanceLine[];
  totalReceivables: number;
  totalPayables: number;
}

interface TrialBalanceLine {
  accountId: string;
  accountCode: string;
  accountName: string;
  accountTypeCode: string;
  normalBalance: 'debit' | 'credit';
  debitTotal: number;
  creditTotal: number;
  balance: number;
}

interface StatementFilters {
  includeZeroLines: boolean;
  minimumAbsAmount: number;
  searchTerm: string;
}

interface CustomStatementReport {
  id: string;
  name: string;
  datePreset: DateRangePreset;
  startDate: string;
  endDate: string;
  scope: StatementScope;
  includeZeroLines: boolean;
  minimumAbsAmount: number;
  searchTerm: string;
}

type ReportControlType = 'text' | 'number' | 'date' | 'select' | 'boolean';

interface NormalizedReportControlOption {
  label: string;
  value: string;
}

interface NormalizedReportControl {
  key: string;
  label: string;
  type: ReportControlType;
  required: boolean;
  options: NormalizedReportControlOption[];
  defaultValue: unknown;
  placeholder: string;
}

interface NormalizedReportRollup {
  key: string;
  label: string;
  valuePath: string;
  format: 'number' | 'currency' | 'percent' | 'string';
  precision: number;
}

interface RenderedRollup {
  key: string;
  label: string;
  displayValue: string;
}

@Component({
    selector: 'app-finance-reports',
    templateUrl: './finance-reports.component.html',
    styleUrls: ['./finance-reports.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FinanceReportsComponent implements OnInit, OnDestroy {
  private readonly customReportsMetadataNamespace = 'financeAdmin';
  private readonly customReportsMetadataKey = 'customStatementReports';
  private readonly destroy$ = new Subject<void>();
  private readonly defaultReportRollups: NormalizedReportRollup[] = [
    { key: 'movementCount', label: 'Movement Count', valuePath: 'movementSummary.movementCount', format: 'number', precision: 0 },
    { key: 'grossIn', label: 'Gross In', valuePath: 'movementSummary.grossIn', format: 'currency', precision: 2 },
    { key: 'grossOut', label: 'Gross Out', valuePath: 'movementSummary.grossOut', format: 'currency', precision: 2 },
    { key: 'totalFees', label: 'Total Fees', valuePath: 'movementSummary.totalFees', format: 'currency', precision: 2 },
    { key: 'netDelta', label: 'Net Delta', valuePath: 'movementSummary.netDelta', format: 'currency', precision: 2 },
  ];

  tabs: FinanceTab[] = FINANCE_TABS;
  activeTab: FinanceTab = this.tabs.find((tab) => tab.id === 'reports') || this.tabs[0];

  readonly rangePresetOptions: Array<{ value: DateRangePreset; label: string }> = [
    { value: 'this-month', label: 'This Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'this-quarter', label: 'This Quarter' },
    { value: 'last-quarter', label: 'Last Quarter' },
    { value: 'this-year', label: 'This Year' },
    { value: 'ytd', label: 'Year to Date' },
    { value: 'last-year', label: 'Last Year' },
    { value: 'last-30', label: 'Last 30 Days' },
    { value: 'last-90', label: 'Last 90 Days' },
    { value: 'trailing-12', label: 'Trailing 12 Months' },
    { value: 'custom', label: 'Custom' },
  ];

  statementPreset: DateRangePreset = 'this-quarter';
  statementStartDate = '';
  statementEndDate = '';
  statementScope: StatementScope = 'both';
  includeZeroLines = false;
  minimumAbsAmount = '';
  accountSearchTerm = '';

  profitLoss: ProfitLossStatement | null = null;
  balanceSheet: BalanceSheetStatement | null = null;
  outstandingBalances: OutstandingPayablesReceivables | null = null;

  customReports: CustomStatementReport[] = [];
  selectedCustomReportId = '';
  customReportName = '';

  contracts: Contract[] = [];
  selectedContractId = '';
  contractPreset: DateRangePreset = 'this-month';
  contractPeriodStart = '';
  contractPeriodEnd = '';
  report: ContractSummaryReport | null = null;
  reportConfig: ContractReportConfig | null = null;
  reportConfigControls: NormalizedReportControl[] = [];
  reportConfigRollups: NormalizedReportRollup[] = [];
  reportControlValues: Record<string, unknown> = {};
  renderedRollups: RenderedRollup[] = [];
  isReportConfigLoading = false;

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private financeLedgerService: FinanceLedgerService,
    private usersService: UsersService,
  ) {}

  ngOnInit(): void {
    this.setActiveTab();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(() => this.setActiveTab());

    this.loadCustomReports();
    this.applyRangePreset(this.statementPreset);
    this.loadFinancialStatements();

    this.applyContractRangePreset(this.contractPreset);

    this.reloadContracts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setActiveTab(): void {
    const parsedUrl = this.router.parseUrl(this.router.url);
    const url = parsedUrl.toString().split('?')[0];
    this.activeTab = this.tabs.find((tab) => tab.link === url) || this.tabs[0];
  }

  gotoTab(tab: FinanceTab): void {
    if (this.activeTab?.id === tab.id) {
      return;
    }

    this.router.navigate([tab.link]).then();
    this.activeTab = tab;
  }

  onRangePresetChange(): void {
    this.applyRangePreset(this.statementPreset);
  }

  onCustomDateChanged(): void {
    if (this.statementPreset !== 'custom') {
      this.statementPreset = 'custom';
    }
  }

  onContractRangePresetChange(): void {
    this.applyContractRangePreset(this.contractPreset);
    this.onReportControlDateChanged();
  }

  onContractCustomDateChanged(): void {
    if (this.contractPreset !== 'custom') {
      this.contractPreset = 'custom';
    }
    this.onReportControlDateChanged();
  }

  onSelectedContractChange(): void {
    this.report = null;
    this.renderedRollups = [];
    this.loadReportConfig();
  }

  onReportControlDateChanged(): void {
    this.report = null;
    this.renderedRollups = [];
  }

  isTextControl(control: NormalizedReportControl): boolean {
    return control.type === 'text';
  }

  isNumberControl(control: NormalizedReportControl): boolean {
    return control.type === 'number';
  }

  isDateControl(control: NormalizedReportControl): boolean {
    return control.type === 'date';
  }

  isSelectControl(control: NormalizedReportControl): boolean {
    return control.type === 'select';
  }

  isBooleanControl(control: NormalizedReportControl): boolean {
    return control.type === 'boolean';
  }

  shouldShowProfitLoss(): boolean {
    return this.statementScope === 'both' || this.statementScope === 'profit-loss';
  }

  shouldShowBalanceSheet(): boolean {
    return this.statementScope === 'both' || this.statementScope === 'balance-sheet';
  }

  saveCustomReport(): void {
    const name = this.customReportName.trim();
    if (!name) {
      this.snackBar.open('Custom report name is required', 'Close', { duration: 3000 });
      return;
    }

    if (!this.statementStartDate || !this.statementEndDate) {
      this.snackBar.open('Set the report date range before saving', 'Close', { duration: 3000 });
      return;
    }

    const reportId = this.generateReportId();
    const definition: CustomStatementReport = {
      id: reportId,
      name,
      datePreset: this.statementPreset,
      startDate: this.statementStartDate,
      endDate: this.statementEndDate,
      scope: this.statementScope,
      includeZeroLines: this.includeZeroLines,
      minimumAbsAmount: this.getMinimumAbsAmount(),
      searchTerm: this.accountSearchTerm.trim(),
    };

    this.customReports.push(definition);

    this.customReports.sort((report1, report2) => report1.name.localeCompare(report2.name));
    this.selectedCustomReportId = definition.id;
    this.persistCustomReports('Custom report saved');
  }

  updateSelectedCustomReport(): void {
    const existingIndex = this.customReports.findIndex(report => report.id === this.selectedCustomReportId);
    if (existingIndex < 0) {
      this.snackBar.open('Select a saved report first', 'Close', { duration: 3000 });
      return;
    }

    const name = this.customReportName.trim();
    if (!name) {
      this.snackBar.open('Custom report name is required', 'Close', { duration: 3000 });
      return;
    }

    if (!this.statementStartDate || !this.statementEndDate) {
      this.snackBar.open('Set the report date range before updating', 'Close', { duration: 3000 });
      return;
    }

    const existing = this.customReports[existingIndex];
    this.customReports[existingIndex] = {
      ...existing,
      name,
      datePreset: this.statementPreset,
      startDate: this.statementStartDate,
      endDate: this.statementEndDate,
      scope: this.statementScope,
      includeZeroLines: this.includeZeroLines,
      minimumAbsAmount: this.getMinimumAbsAmount(),
      searchTerm: this.accountSearchTerm.trim(),
    };

    this.customReports.sort((report1, report2) => report1.name.localeCompare(report2.name));
    this.persistCustomReports('Custom report updated');
  }

  applySelectedCustomReport(): void {
    const selected = this.customReports.find(report => report.id === this.selectedCustomReportId);
    if (!selected) {
      this.snackBar.open('Select a saved report first', 'Close', { duration: 3000 });
      return;
    }

    this.customReportName = selected.name;
    this.statementPreset = selected.datePreset;
    this.statementScope = selected.scope;
    this.includeZeroLines = selected.includeZeroLines;
    this.minimumAbsAmount = selected.minimumAbsAmount > 0 ? selected.minimumAbsAmount.toString() : '';
    this.accountSearchTerm = selected.searchTerm || '';

    if (selected.datePreset === 'custom') {
      this.statementStartDate = selected.startDate;
      this.statementEndDate = selected.endDate;
    } else {
      this.applyRangePreset(selected.datePreset);
    }

    this.loadFinancialStatements();
  }

  deleteSelectedCustomReport(): void {
    const existingIndex = this.customReports.findIndex(report => report.id === this.selectedCustomReportId);
    if (existingIndex < 0) {
      this.snackBar.open('Select a saved report first', 'Close', { duration: 3000 });
      return;
    }

    this.customReports.splice(existingIndex, 1);
    this.persistCustomReports('Custom report deleted');
    this.selectedCustomReportId = '';
  }

  downloadProfitLossCsv(): void {
    if (!this.profitLoss) {
      this.snackBar.open('Run the Profit & Loss report first', 'Close', { duration: 3000 });
      return;
    }

    const rows: string[][] = [
      ['Section', 'Account Code', 'Account Name', 'Amount'],
      ...this.profitLoss.revenueLines.map(line => ['Revenue', line.accountCode, line.accountName, this.formatAmount(line.amount)]),
      ['Revenue', '', 'Total Revenue', this.formatAmount(this.profitLoss.totalRevenue)],
      ...this.profitLoss.expenseLines.map(line => ['Expenses', line.accountCode, line.accountName, this.formatAmount(line.amount)]),
      ['Expenses', '', 'Total Expenses', this.formatAmount(this.profitLoss.totalExpenses)],
      ['Summary', '', 'Net Income', this.formatAmount(this.profitLoss.netIncome)],
    ];

    const filename = `profit-loss-${this.statementStartDate}-to-${this.statementEndDate}.csv`;
    this.downloadCsv(filename, rows);
  }

  downloadBalanceSheetCsv(): void {
    if (!this.balanceSheet) {
      this.snackBar.open('Run the Balance Sheet report first', 'Close', { duration: 3000 });
      return;
    }

    const rows: string[][] = [
      ['Section', 'Account Code', 'Account Name', 'Amount'],
      ...this.balanceSheet.assetLines.map(line => ['Assets', line.accountCode, line.accountName, this.formatAmount(line.amount)]),
      ['Assets', '', 'Total Assets', this.formatAmount(this.balanceSheet.totalAssets)],
      ...this.balanceSheet.liabilityLines.map(line => ['Liabilities', line.accountCode, line.accountName, this.formatAmount(line.amount)]),
      ['Liabilities', '', 'Total Liabilities', this.formatAmount(this.balanceSheet.totalLiabilities)],
      ['Equity', '', 'Current Earnings', this.formatAmount(this.balanceSheet.currentEarnings)],
      ['Summary', '', 'Total Liabilities + Equity', this.formatAmount(this.balanceSheet.totalLiabilitiesAndEquity)],
      ['Summary', '', 'Balance Check', this.formatAmount(this.balanceSheet.imbalance)],
    ];

    const filename = `balance-sheet-as-of-${this.statementEndDate}.csv`;
    this.downloadCsv(filename, rows);
  }

  downloadOutstandingBalancesCsv(): void {
    if (!this.outstandingBalances) {
      this.snackBar.open('Run financial statements first', 'Close', { duration: 3000 });
      return;
    }

    const rows: string[][] = [
      ['Section', 'Account Code', 'Account Name', 'Amount'],
      ...this.outstandingBalances.receivableLines.map(line => ['Accounts Receivable', line.accountCode, line.accountName, this.formatAmount(line.amount)]),
      ['Accounts Receivable', '', 'Total Receivables', this.formatAmount(this.outstandingBalances.totalReceivables)],
      ...this.outstandingBalances.payableLines.map(line => ['Accounts Payable', line.accountCode, line.accountName, this.formatAmount(line.amount)]),
      ['Accounts Payable', '', 'Total Payables', this.formatAmount(this.outstandingBalances.totalPayables)],
    ];

    const filename = `outstanding-ar-ap-as-of-${this.outstandingBalances.asOfDate}.csv`;
    this.downloadCsv(filename, rows);
  }

  loadFinancialStatements(): void {
    if (!this.statementStartDate || !this.statementEndDate) {
      this.snackBar.open('Select a valid date range first', 'Close', { duration: 3000 });
      return;
    }

    const rangeStart = new Date(`${this.statementStartDate}T00:00:00.000Z`);
    const rangeEnd = new Date(`${this.statementEndDate}T23:59:59.999Z`);
    if (rangeEnd.getTime() < rangeStart.getTime()) {
      this.snackBar.open('End date must be on or after start date', 'Close', { duration: 3000 });
      return;
    }

    const priorEnd = new Date(rangeStart.getTime() - 1);
    const filters = this.getStatementFilters();

    Promise.all([
      this.financeLedgerService.getTrialBalance(rangeEnd.toISOString()),
      this.financeLedgerService.getTrialBalance(priorEnd.toISOString()),
    ]).then(([endTrialBalance, priorTrialBalance]) => {
      const endLines = this.parseTrialBalanceLines(endTrialBalance);
      const priorLines = this.parseTrialBalanceLines(priorTrialBalance);
      const priorByAccountId = new Map(priorLines.map(line => [line.accountId, line] as [string, TrialBalanceLine]));

      this.profitLoss = this.buildProfitAndLoss(endLines, priorByAccountId, filters);
      this.balanceSheet = this.buildBalanceSheet(endLines, filters);
      this.outstandingBalances = this.buildOutstandingBalances(endLines, this.statementEndDate);
    }).catch((error: unknown) => {
      this.profitLoss = null;
      this.balanceSheet = null;
      this.outstandingBalances = null;
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load financial statements'), 'Close', { duration: 3000 });
    });
  }

  reloadContracts(): void {
    this.financeLedgerService.listContracts().then(contracts => {
      this.contracts = contracts.sort((contract1, contract2) => (contract1.name || '').localeCompare(contract2.name || ''));
      if (!this.selectedContractId && this.contracts.length) {
        this.selectedContractId = this.contracts[0].id;
      }
      if (this.selectedContractId) {
        this.loadReportConfig();
      }
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load contracts'), 'Close', { duration: 3000 });
    });
  }

  reloadReportConfig(): void {
    this.loadReportConfig(true);
  }

  loadReport(): void {
    if (!this.selectedContractId || !this.contractPeriodStart || !this.contractPeriodEnd) {
      this.snackBar.open('Select a contract and date range first', 'Close', { duration: 3000 });
      return;
    }

    const startDate = new Date(`${this.contractPeriodStart}T00:00:00.000Z`);
    const endDate = new Date(`${this.contractPeriodEnd}T23:59:59.999Z`);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      this.snackBar.open('Report period must contain valid dates', 'Close', { duration: 3000 });
      return;
    }

    if (endDate.getTime() < startDate.getTime()) {
      this.snackBar.open('Period end must be on or after period start', 'Close', { duration: 3000 });
      return;
    }

    const startIso = startDate.toISOString();
    const endIso = endDate.toISOString();

    const dynamicQuery = this.getDynamicSummaryQueryParams();

    this.financeLedgerService.getContractSummary(this.selectedContractId, startIso, endIso, dynamicQuery).then(report => {
      this.report = report;
      this.renderedRollups = this.buildRenderedRollups(report);
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load contract summary report'), 'Close', { duration: 3000 });
    });
  }

  private loadReportConfig(showSuccessMessage = false): void {
    if (!this.selectedContractId) {
      this.reportConfig = null;
      this.reportConfigControls = [];
      this.reportConfigRollups = [];
      this.reportControlValues = {};
      return;
    }

    this.isReportConfigLoading = true;
    this.financeLedgerService.getContractReportConfig(this.selectedContractId).then((config) => {
      this.isReportConfigLoading = false;
      this.reportConfig = this.toObject(config) as ContractReportConfig;
      this.reportConfigControls = this.normalizeReportControls(this.reportConfig);
      this.reportConfigRollups = this.normalizeReportRollups(this.reportConfig);
      this.reportControlValues = this.getInitialReportControlValues(this.reportConfigControls);

      if (this.report) {
        this.renderedRollups = this.buildRenderedRollups(this.report);
      }

      if (showSuccessMessage) {
        this.snackBar.open('Report config refreshed', 'Close', { duration: 2000 });
      }
    }).catch((error: unknown) => {
      this.isReportConfigLoading = false;
      this.reportConfig = null;
      this.reportConfigControls = [];
      this.reportConfigRollups = [];
      this.reportControlValues = {};
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to load report config'), 'Close', { duration: 3000 });
    });
  }

  private normalizeReportControls(config: ContractReportConfig): NormalizedReportControl[] {
    const rawControls = this.resolveConfigArray(config, ['controls', 'filters', 'reportControls', 'queryControls', 'parameters']);
    const normalizedControls: NormalizedReportControl[] = [];

    rawControls.forEach((rawControl, index) => {
      const keyCandidate = this.getString(rawControl.key)
        || this.getString(rawControl.id)
        || this.getString(rawControl.name)
        || this.getString(rawControl.field)
        || this.getString(rawControl.param);

      if (!keyCandidate) {
        return;
      }

      const key = keyCandidate.trim();
      const type = this.normalizeReportControlType(
        this.getString(rawControl.type)
        || this.getString(rawControl.controlType)
        || this.getString(rawControl.inputType),
        rawControl,
      );

      const options = this.normalizeReportControlOptions(rawControl);
      const defaultValue = rawControl.defaultValue ?? rawControl.default ?? rawControl.value ?? null;
      const label = this.getString(rawControl.label)
        || this.getString(rawControl.title)
        || this.getString(rawControl.name)
        || this.humanizeKey(key);

      normalizedControls.push({
        key,
        label,
        type,
        required: Boolean(rawControl.required),
        options,
        defaultValue: defaultValue !== null ? defaultValue : this.getFallbackControlDefault(type, options),
        placeholder: this.getString(rawControl.placeholder),
      });
    });

    return normalizedControls.filter((control, index, controls) => {
      return controls.findIndex(candidate => candidate.key === control.key) === index;
    });
  }

  private normalizeReportRollups(config: ContractReportConfig): NormalizedReportRollup[] {
    const rawRollups = this.resolveConfigArray(config, ['rollups', 'kpis', 'metrics']);

    if (!rawRollups.length) {
      return [];
    }

    const normalizedRollups: NormalizedReportRollup[] = rawRollups.map((rawRollup) => {
      const keyCandidate = this.getString(rawRollup.key)
        || this.getString(rawRollup.id)
        || this.getString(rawRollup.name)
        || this.getString(rawRollup.metric)
        || this.getString(rawRollup.field)
        || this.getString(rawRollup.path)
        || '';

      const valuePath = this.getString(rawRollup.valuePath)
        || this.getString(rawRollup.path)
        || this.getString(rawRollup.field)
        || this.getString(rawRollup.metricPath)
        || keyCandidate;

      const format = this.normalizeRollupFormat(this.getString(rawRollup.format) || this.getString(rawRollup.type));
      const precisionValue = Number(rawRollup.precision);
      const precision = Number.isFinite(precisionValue) ? Math.max(0, Math.round(precisionValue)) : (format === 'number' ? 0 : 2);

      return {
        key: keyCandidate || valuePath || `rollup-${Math.random().toString(16).slice(2, 8)}`,
        label: this.getString(rawRollup.label) || this.getString(rawRollup.title) || this.humanizeKey(keyCandidate || valuePath),
        valuePath,
        format,
        precision,
      };
    });

    return normalizedRollups.filter((rollup, index, rollups) => {
      return rollup.valuePath && rollups.findIndex(candidate => candidate.key === rollup.key) === index;
    });
  }

  private resolveConfigArray(config: ContractReportConfig, keyCandidates: string[]): Array<Record<string, unknown>> {
    const rootObject = this.toObject(config);
    const nestedCandidates: Array<Record<string, unknown>> = [
      rootObject,
      this.toObject(rootObject.summary),
      this.toObject(rootObject.report),
      this.toObject(rootObject.ui),
      this.toObject(rootObject.contract),
    ];

    for (const objectCandidate of nestedCandidates) {
      for (const keyCandidate of keyCandidates) {
        const value = objectCandidate[keyCandidate];
        if (Array.isArray(value)) {
          return value
            .filter(item => this.isJsonObject(item))
            .map(item => item as Record<string, unknown>);
        }
      }
    }

    return [];
  }

  private getInitialReportControlValues(controls: NormalizedReportControl[]): Record<string, unknown> {
    const nextValues: Record<string, unknown> = {};

    controls.forEach((control) => {
      const currentValue = this.reportControlValues[control.key];
      const value = currentValue !== undefined ? currentValue : control.defaultValue;

      switch (control.type) {
        case 'boolean':
          nextValues[control.key] = Boolean(value);
          break;
        case 'number': {
          const parsed = Number(value);
          nextValues[control.key] = Number.isFinite(parsed) ? parsed : null;
          break;
        }
        case 'date':
          nextValues[control.key] = this.normalizeDateInputValue(value);
          break;
        case 'select': {
          const optionValues = control.options.map(option => option.value);
          const candidate = this.getString(value);
          if (candidate && optionValues.includes(candidate)) {
            nextValues[control.key] = candidate;
            break;
          }
          nextValues[control.key] = control.required && optionValues.length ? optionValues[0] : '';
          break;
        }
        case 'text':
        default:
          nextValues[control.key] = this.getString(value);
          break;
      }
    });

    return nextValues;
  }

  private getDynamicSummaryQueryParams(): Record<string, unknown> {
    const query: Record<string, unknown> = {};

    this.reportConfigControls.forEach((control) => {
      const value = this.reportControlValues[control.key];
      if (value === undefined || value === null) {
        return;
      }

      if (control.type === 'boolean') {
        query[control.key] = Boolean(value);
        return;
      }

      if (control.type === 'number') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          query[control.key] = parsed;
        }
        return;
      }

      const stringValue = this.getString(value);
      if (!stringValue) {
        return;
      }

      query[control.key] = stringValue;
    });

    return query;
  }

  private buildRenderedRollups(report: ContractSummaryReport): RenderedRollup[] {
    const rollups = this.reportConfigRollups.length ? this.reportConfigRollups : this.defaultReportRollups;

    return rollups.map((rollup) => {
      const rawValue = this.getValueByPath(report, rollup.valuePath);
      return {
        key: rollup.key,
        label: rollup.label,
        displayValue: this.formatRollupValue(rawValue, rollup.format, rollup.precision),
      };
    });
  }

  private normalizeReportControlType(rawType: string, rawControl: Record<string, unknown>): ReportControlType {
    const normalizedType = (rawType || '').toLowerCase();
    if (normalizedType.includes('bool') || normalizedType.includes('toggle') || normalizedType.includes('switch')) {
      return 'boolean';
    }
    if (normalizedType.includes('number') || normalizedType.includes('int') || normalizedType.includes('float') || normalizedType.includes('decimal')) {
      return 'number';
    }
    if (normalizedType.includes('date') || normalizedType.includes('time')) {
      return 'date';
    }
    if (normalizedType.includes('select') || normalizedType.includes('drop') || normalizedType.includes('enum')) {
      return 'select';
    }

    const options = this.normalizeReportControlOptions(rawControl);
    return options.length ? 'select' : 'text';
  }

  private normalizeReportControlOptions(rawControl: Record<string, unknown>): NormalizedReportControlOption[] {
    const rawOptions = [rawControl.options, rawControl.values, rawControl.choices]
      .find(candidate => Array.isArray(candidate)) as Array<unknown> | undefined;

    if (!rawOptions?.length) {
      return [];
    }

    return rawOptions.map((option) => {
      if (this.isJsonObject(option)) {
        const optionObject = option as Record<string, unknown>;
        const value = this.getString(optionObject.value || optionObject.id || optionObject.key || optionObject.name);
        const label = this.getString(optionObject.label || optionObject.title || optionObject.name || value);
        return {
          label: label || value,
          value,
        };
      }

      const value = this.getString(option);
      return {
        label: value,
        value,
      };
    }).filter((option) => option.value);
  }

  private normalizeRollupFormat(value: string): 'number' | 'currency' | 'percent' | 'string' {
    const normalizedValue = (value || '').toLowerCase();
    if (normalizedValue.includes('curr') || normalizedValue.includes('money') || normalizedValue.includes('usd') || normalizedValue.includes('dollar')) {
      return 'currency';
    }
    if (normalizedValue.includes('percent') || normalizedValue.includes('ratio')) {
      return 'percent';
    }
    if (normalizedValue.includes('number') || normalizedValue.includes('count') || normalizedValue.includes('int') || normalizedValue.includes('decimal')) {
      return 'number';
    }
    return 'string';
  }

  private getFallbackControlDefault(type: ReportControlType, options: NormalizedReportControlOption[]): unknown {
    if (type === 'boolean') {
      return false;
    }
    if (type === 'select') {
      return options.length ? options[0].value : '';
    }
    if (type === 'number') {
      return null;
    }
    return '';
  }

  private normalizeDateInputValue(value: unknown): string {
    const rawValue = this.getString(value);
    if (!rawValue) {
      return '';
    }
    const parsedValue = new Date(rawValue);
    if (Number.isNaN(parsedValue.getTime())) {
      return '';
    }
    return parsedValue.toISOString().slice(0, 10);
  }

  private getValueByPath(payload: unknown, path: string): unknown {
    const safePath = String(path || '').trim();
    if (!safePath) {
      return null;
    }

    const keys = safePath.split('.').map(value => value.trim()).filter(Boolean);
    let current: any = payload;

    for (const key of keys) {
      if (current === null || current === undefined) {
        return null;
      }
      current = current[key];
    }

    return current;
  }

  private formatRollupValue(value: unknown, format: 'number' | 'currency' | 'percent' | 'string', precision: number): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (format === 'string') {
      return String(value);
    }

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return String(value);
    }

    if (format === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(numericValue);
    }

    if (format === 'percent') {
      return `${(numericValue * 100).toFixed(precision)}%`;
    }

    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(numericValue);
  }

  private humanizeKey(value: string): string {
    return value
      .replace(/[_\-]+/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  private getString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value).trim();
  }

  private applyRangePreset(preset: DateRangePreset): void {
    const range = this.getRangeForPreset(preset);
    if (!range) {
      return;
    }

    this.statementStartDate = this.toIsoDate(range.start);
    this.statementEndDate = this.toIsoDate(range.end);
  }

  private applyContractRangePreset(preset: DateRangePreset): void {
    const range = this.getRangeForPreset(preset);
    if (!range) {
      return;
    }

    this.contractPeriodStart = this.toIsoDate(range.start);
    this.contractPeriodEnd = this.toIsoDate(range.end);
  }

  private getRangeForPreset(preset: DateRangePreset): { start: Date; end: Date } | null {
    if (preset === 'custom') {
      return null;
    }

    const now = new Date();
    const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

    let start: Date;
    let end: Date;

    switch (preset) {
      case 'this-month':
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), 1));
        end = todayUtc;
        break;
      case 'last-month':
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth() - 1, 1));
        end = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), 0));
        break;
      case 'this-quarter': {
        const quarterStartMonth = Math.floor(todayUtc.getUTCMonth() / 3) * 3;
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), quarterStartMonth, 1));
        end = todayUtc;
        break;
      }
      case 'last-quarter': {
        const quarterStartMonth = Math.floor(todayUtc.getUTCMonth() / 3) * 3;
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), quarterStartMonth - 3, 1));
        end = new Date(Date.UTC(todayUtc.getUTCFullYear(), quarterStartMonth, 0));
        break;
      }
      case 'ytd':
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), 0, 1));
        end = todayUtc;
        break;
      case 'this-year':
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), 0, 1));
        end = new Date(Date.UTC(todayUtc.getUTCFullYear(), 11, 31));
        break;
      case 'last-year':
        start = new Date(Date.UTC(todayUtc.getUTCFullYear() - 1, 0, 1));
        end = new Date(Date.UTC(todayUtc.getUTCFullYear() - 1, 11, 31));
        break;
      case 'last-30':
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), todayUtc.getUTCDate() - 29));
        end = todayUtc;
        break;
      case 'last-90':
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), todayUtc.getUTCDate() - 89));
        end = todayUtc;
        break;
      case 'trailing-12':
        start = new Date(Date.UTC(todayUtc.getUTCFullYear() - 1, todayUtc.getUTCMonth(), todayUtc.getUTCDate() + 1));
        end = todayUtc;
        break;
      default:
        start = new Date(Date.UTC(todayUtc.getUTCFullYear(), todayUtc.getUTCMonth(), 1));
        end = todayUtc;
        break;
    }

    return { start, end };
  }

  private toIsoDate(date: Date): string {
    return date.toISOString().slice(0, 10);
  }

  private parseTrialBalanceLines(payload: unknown): TrialBalanceLine[] {
    const lines = (payload as any)?.lines;
    if (!Array.isArray(lines)) {
      return [];
    }

    return lines.map((line) => {
      const accountTypeCode = String(line?.accountTypeCode || '').trim();
      const normalFromType = accountTypeCode === 'assets' || accountTypeCode === 'expenses' ? 'debit' : 'credit';
      const normalBalance = line?.normalBalance === 'credit' || line?.normalBalance === 'debit'
        ? line.normalBalance
        : normalFromType;

      return {
        accountId: String(line?.accountId || '').trim(),
        accountCode: String(line?.accountCode || '').trim(),
        accountName: String(line?.accountName || '').trim(),
        accountTypeCode,
        normalBalance,
        debitTotal: this.roundAmount(Number(line?.debitTotal || 0)),
        creditTotal: this.roundAmount(Number(line?.creditTotal || 0)),
        balance: this.roundAmount(Number(line?.balance || 0)),
      } as TrialBalanceLine;
    }).filter((line) => line.accountId && line.accountTypeCode);
  }

  private buildProfitAndLoss(
    endLines: TrialBalanceLine[],
    priorByAccountId: Map<string, TrialBalanceLine>,
    filters: StatementFilters,
  ): ProfitLossStatement {
    const revenueLines: StatementLine[] = [];
    const expenseLines: StatementLine[] = [];

    endLines.forEach((endLine) => {
      if (endLine.accountTypeCode !== 'revenue' && endLine.accountTypeCode !== 'expenses') {
        return;
      }

      const prior = priorByAccountId.get(endLine.accountId);
      const periodDebit = this.roundAmount(endLine.debitTotal - Number(prior?.debitTotal || 0));
      const periodCredit = this.roundAmount(endLine.creditTotal - Number(prior?.creditTotal || 0));
      const amount = endLine.normalBalance === 'debit'
        ? this.roundAmount(periodDebit - periodCredit)
        : this.roundAmount(periodCredit - periodDebit);

      if (!this.passesLineFilters(endLine.accountCode, endLine.accountName, amount, filters)) {
        return;
      }

      const statementLine: StatementLine = {
        accountId: endLine.accountId,
        accountCode: endLine.accountCode,
        accountName: endLine.accountName,
        accountTypeCode: endLine.accountTypeCode,
        amount,
      };

      if (endLine.accountTypeCode === 'revenue') {
        revenueLines.push(statementLine);
      } else {
        expenseLines.push(statementLine);
      }
    });

    this.sortStatementLines(revenueLines);
    this.sortStatementLines(expenseLines);

    const totalRevenue = this.roundAmount(revenueLines.reduce((sum, line) => sum + line.amount, 0));
    const totalExpenses = this.roundAmount(expenseLines.reduce((sum, line) => sum + line.amount, 0));

    return {
      revenueLines,
      expenseLines,
      totalRevenue,
      totalExpenses,
      netIncome: this.roundAmount(totalRevenue - totalExpenses),
    };
  }

  private buildBalanceSheet(endLines: TrialBalanceLine[], filters: StatementFilters): BalanceSheetStatement {
    const assetLines: StatementLine[] = [];
    const liabilityLines: StatementLine[] = [];

    let revenueToDate = 0;
    let expensesToDate = 0;

    endLines.forEach((line) => {
      if (line.accountTypeCode === 'assets') {
        if (this.passesLineFilters(line.accountCode, line.accountName, line.balance, filters)) {
          assetLines.push({
            accountId: line.accountId,
            accountCode: line.accountCode,
            accountName: line.accountName,
            accountTypeCode: line.accountTypeCode,
            amount: line.balance,
          });
        }
        return;
      }

      if (line.accountTypeCode === 'liabilities') {
        if (this.passesLineFilters(line.accountCode, line.accountName, line.balance, filters)) {
          liabilityLines.push({
            accountId: line.accountId,
            accountCode: line.accountCode,
            accountName: line.accountName,
            accountTypeCode: line.accountTypeCode,
            amount: line.balance,
          });
        }
        return;
      }

      if (line.accountTypeCode === 'revenue') {
        revenueToDate += line.balance;
      }
      if (line.accountTypeCode === 'expenses') {
        expensesToDate += line.balance;
      }
    });

    this.sortStatementLines(assetLines);
    this.sortStatementLines(liabilityLines);

    const totalAssets = this.roundAmount(assetLines.reduce((sum, line) => sum + line.amount, 0));
    const totalLiabilities = this.roundAmount(liabilityLines.reduce((sum, line) => sum + line.amount, 0));
    const currentEarnings = this.roundAmount(revenueToDate - expensesToDate);
    const totalLiabilitiesAndEquity = this.roundAmount(totalLiabilities + currentEarnings);
    const imbalance = this.roundAmount(totalAssets - totalLiabilitiesAndEquity);

    return {
      assetLines,
      liabilityLines,
      totalAssets,
      totalLiabilities,
      currentEarnings,
      totalLiabilitiesAndEquity,
      imbalance,
      isBalanced: Math.abs(imbalance) < 0.005,
    };
  }

  private sortStatementLines(lines: StatementLine[]): void {
    lines.sort((line1, line2) => {
      const value1 = `${line1.accountCode || ''} ${line1.accountName || line1.accountId}`.toLowerCase();
      const value2 = `${line2.accountCode || ''} ${line2.accountName || line2.accountId}`.toLowerCase();
      if (value1 > value2) {
        return 1;
      }
      if (value1 < value2) {
        return -1;
      }
      return 0;
    });
  }

  private buildOutstandingBalances(endLines: TrialBalanceLine[], asOfDate: string): OutstandingPayablesReceivables {
    const receivableLines = endLines
      .filter((line) => line.accountTypeCode === 'assets' && this.isReceivableAccount(line))
      .map((line) => ({
        accountId: line.accountId,
        accountCode: line.accountCode,
        accountName: line.accountName,
        amount: this.roundAmount(line.balance),
      }))
      .filter((line) => Math.abs(line.amount) >= 0.005);

    const payableLines = endLines
      .filter((line) => line.accountTypeCode === 'liabilities' && this.isPayableAccount(line))
      .map((line) => ({
        accountId: line.accountId,
        accountCode: line.accountCode,
        accountName: line.accountName,
        amount: this.roundAmount(line.balance),
      }))
      .filter((line) => Math.abs(line.amount) >= 0.005);

    receivableLines.sort((line1, line2) => this.compareAccountValues(line1.accountCode, line1.accountName, line2.accountCode, line2.accountName));
    payableLines.sort((line1, line2) => this.compareAccountValues(line1.accountCode, line1.accountName, line2.accountCode, line2.accountName));

    return {
      asOfDate,
      receivableLines,
      payableLines,
      totalReceivables: this.roundAmount(receivableLines.reduce((sum, line) => sum + line.amount, 0)),
      totalPayables: this.roundAmount(payableLines.reduce((sum, line) => sum + line.amount, 0)),
    };
  }

  private isReceivableAccount(line: TrialBalanceLine): boolean {
    const label = `${line.accountCode} ${line.accountName}`.toLowerCase();
    return /(^|\b)(a\/?r|ar)(\b|$)/i.test(label) ||
      label.includes('receivable') ||
      label.includes('accounts receivable');
  }

  private isPayableAccount(line: TrialBalanceLine): boolean {
    const label = `${line.accountCode} ${line.accountName}`.toLowerCase();
    return /(^|\b)(a\/?p|ap)(\b|$)/i.test(label) ||
      label.includes('payable') ||
      label.includes('accounts payable');
  }

  private compareAccountValues(code1: string, name1: string, code2: string, name2: string): number {
    const value1 = `${code1 || ''} ${name1 || ''}`.toLowerCase();
    const value2 = `${code2 || ''} ${name2 || ''}`.toLowerCase();
    if (value1 > value2) {
      return 1;
    }
    if (value1 < value2) {
      return -1;
    }
    return 0;
  }

  private getStatementFilters(): StatementFilters {
    return {
      includeZeroLines: this.includeZeroLines,
      minimumAbsAmount: this.getMinimumAbsAmount(),
      searchTerm: this.accountSearchTerm.trim().toLowerCase(),
    };
  }

  private getMinimumAbsAmount(): number {
    const parsed = Number(this.minimumAbsAmount);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return 0;
    }
    return this.roundAmount(parsed);
  }

  private passesLineFilters(accountCode: string, accountName: string, amount: number, filters: StatementFilters): boolean {
    const absoluteAmount = Math.abs(amount);
    if (!filters.includeZeroLines && absoluteAmount < 0.005) {
      return false;
    }
    if (absoluteAmount + 0.0001 < filters.minimumAbsAmount) {
      return false;
    }

    if (filters.searchTerm) {
      const searchable = `${accountCode} ${accountName}`.toLowerCase();
      if (!searchable.includes(filters.searchTerm)) {
        return false;
      }
    }

    return true;
  }

  private loadCustomReports(): void {
    const userId = this.usersService.getCurrentUserId();
    if (!userId) {
      this.customReports = [];
      return;
    }

    this.usersService.getUserById(userId).then((result) => {
      const user = this.extractUser(result);
      const metadata = this.toObject(user?.metadata);
      const namespace = this.toObject(metadata[this.customReportsMetadataNamespace]);
      const rawReports = namespace[this.customReportsMetadataKey];
      const parsed = Array.isArray(rawReports) ? rawReports : [];

      this.customReports = parsed
        .map((item) => this.toCustomReport(item))
        .filter((report): report is CustomStatementReport => Boolean(report))
        .sort((report1, report2) => report1.name.localeCompare(report2.name));
    }).catch(() => {
      this.customReports = [];
    });
  }

  private persistCustomReports(successMessage: string): void {
    const userId = this.usersService.getCurrentUserId();
    if (!userId) {
      this.snackBar.open('Unable to save custom reports: no current user', 'Close', { duration: 3000 });
      return;
    }

    this.usersService.getUserById(userId).then((result) => {
      const user = this.extractUser(result);
      const existingMetadata = this.toObject(user?.metadata);
      const existingNamespace = this.toObject(existingMetadata[this.customReportsMetadataNamespace]);

      const nextMetadata: Record<string, unknown> = {
        ...existingMetadata,
        [this.customReportsMetadataNamespace]: {
          ...existingNamespace,
          [this.customReportsMetadataKey]: this.customReports,
        },
      };

      return this.usersService.updateUser(userId, { metadata: nextMetadata });
    }).then(() => {
      this.usersService.reloadMe();
      this.snackBar.open(successMessage, 'Close', { duration: 2000 });
    }).catch((error: unknown) => {
      this.snackBar.open(this.financeLedgerService.getErrorMessage(error, 'Unable to save custom reports'), 'Close', { duration: 3000 });
      this.loadCustomReports();
    });
  }

  private extractUser(payload: unknown): any {
    const response = payload as any;
    return response?.user ?? response ?? {};
  }

  private isJsonObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  private toObject(value: unknown): Record<string, unknown> {
    if (this.isJsonObject(value)) {
      return value as Record<string, unknown>;
    }
    return {};
  }

  private toCustomReport(item: any): CustomStatementReport | null {
    const id = String(item?.id || '').trim();
    const name = String(item?.name || '').trim();
    if (!id || !name) {
      return null;
    }

    const datePreset = this.isDateRangePreset(item?.datePreset) ? item.datePreset : 'custom';
    const scope = this.isStatementScope(item?.scope) ? item.scope : 'both';
    const startDate = String(item?.startDate || '').trim();
    const endDate = String(item?.endDate || '').trim();

    return {
      id,
      name,
      datePreset,
      startDate,
      endDate,
      scope,
      includeZeroLines: Boolean(item?.includeZeroLines),
      minimumAbsAmount: Number.isFinite(Number(item?.minimumAbsAmount)) ? Math.max(0, Number(item.minimumAbsAmount)) : 0,
      searchTerm: String(item?.searchTerm || ''),
    };
  }

  private isDateRangePreset(value: unknown): value is DateRangePreset {
    return this.rangePresetOptions.some(option => option.value === value);
  }

  private isStatementScope(value: unknown): value is StatementScope {
    return value === 'both' || value === 'profit-loss' || value === 'balance-sheet';
  }

  private generateReportId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  }

  private downloadCsv(filename: string, rows: string[][]): void {
    const content = rows
      .map(row => row.map(value => this.escapeCsv(value)).join(','))
      .join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private escapeCsv(value: string): string {
    const text = String(value ?? '');
    if (text.includes(',') || text.includes('"') || text.includes('\n')) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  }

  private formatAmount(amount: number): string {
    return this.roundAmount(amount).toFixed(2);
  }

  private roundAmount(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}
